import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useSession } from "@/app/session";
import { Shell } from "@/components/Shell";
import { Alert, Button, Field, SectionTitle } from "@/components/ui";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

interface Data {
  members: { id: string; name: string; email: string; role: string; since: string }[];
  pending: { id: string; email: string; role: string; expiresAt: string }[];
}

export default function Team() {
  const t = useT();
  const { me } = useSession();
  const [data, setData] = useState<Data | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(async () => setData(await api<Data>("/api/workspace/members")), []);
  useEffect(() => {
    void load();
  }, [load]);
  const canInvite = me?.staffRole === "owner" || me?.staffRole === "admin";

  async function invite(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/api/workspace/invites", { method: "POST", json: { email, role } });
      setMsg(t.inviteSent);
      setEmail("");
      await load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <div className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="display mb-6 text-4xl">.{t.team}</h1>
        {canInvite ? (
          <form onSubmit={invite} className="mb-8 flex flex-wrap items-end gap-2">
            <Field
              label={t.inviteStaff}
              name="email"
              type="email"
              required
              placeholder="persona@agencia.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="min-w-60 flex-1"
            />
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold">{t.role}</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as "member" | "admin")}
                className="rounded-sm border border-border bg-panel-2 px-3 py-2.5"
              >
                <option value="member">{t.roles.member}</option>
                <option value="admin">{t.roles.admin}</option>
              </select>
            </label>
            <Button type="submit" loading={busy}>
              {t.invite}
            </Button>
          </form>
        ) : null}
        {msg ? (
          <div className="mb-6">
            <Alert kind="success">{msg}</Alert>
          </div>
        ) : null}
        <SectionTitle>{t.members}</SectionTitle>
        <ul className="mb-8 divide-y divide-border">
          {data?.members.map((m) => (
            <li key={m.id} className="flex items-center gap-3 py-2 text-sm">
              <span className="min-w-0 flex-1 truncate">
                <span className="font-semibold">{m.name}</span> <span className="text-dim">{m.email}</span>
              </span>
              <span className="text-muted">{t.roles[m.role]}</span>
            </li>
          ))}
        </ul>
        <SectionTitle>{t.pending}</SectionTitle>
        <ul className="divide-y divide-border">
          {data?.pending.length === 0 ? <li className="py-2 text-sm text-dim">—</li> : null}
          {data?.pending.map((p) => (
            <li key={p.id} className="flex items-center gap-3 py-2 text-sm">
              <span className="flex-1">{p.email}</span>
              <span className="text-muted">{t.roles[p.role]}</span>
            </li>
          ))}
        </ul>
      </div>
    </Shell>
  );
}
